import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApplicationQuery } from '@app/store/application/application.query';
import { BrandConfigQuery } from '@app/store/brand/brand.query';
import { formatPhoneNumber } from '@app/utils/phone-number-formatter';
import { InsuranceType } from '@models/InsuranceType';
import { Observable, Subject, first, firstValueFrom, map, takeUntil } from 'rxjs';

import { ROUTES } from '../../constants/routes';
import { CoverageOption } from '../../models/CoverageOption';
import { Pet } from '../../models/Pet';
import { OrchestratorService } from '../../services/orchestrator/orchestrator.service';
import { QuoteSessionQuery } from '../../store/session/session.query';
import { QuoteSessionService } from '../../store/session/session.service';

interface ModalData {
	activePet: Observable<Pet>;
	planModalityDisplay: string;
}

@Component({
	selector: 'qe-accident-only-policy-option-modal',
	templateUrl: './policy-option-modal.component.html',
	styleUrls: ['./policy-option-modal.component.scss']
})
export class PolicyOptionModalComponent implements OnInit, OnDestroy {
	@Input() isRxCoverageAmountVisible: boolean = false;
	public readonly accidentIllness: InsuranceType = InsuranceType.AccidentIllness;
	public readonly accidentOnly: InsuranceType = InsuranceType.AccidentOnly;
	public readonly recalculating$: Subject<boolean> = new Subject();
	public readonly brandUnformattedPhoneNumber$: Observable<string | undefined>;
	public readonly brandFormattedPhoneNumber$: Observable<string | undefined>;
	public reimbursementLimit!: CoverageOption[];
	public annualDeductible!: CoverageOption[];
	public reimbursementPercent!: CoverageOption[];
	public policyOptionsForm!: FormGroup;
	public isRxCoverageToggledOn: boolean = false;
	public isExamFeesToggledOn: boolean = false;
	public isExamFeesVisible: boolean = false;
	public isRxCoverageVisible: boolean = false;
	public showAdditionalRxCoverageInfo: boolean = false;
	public showAdditionalExamFeesInfo: boolean = false;
	public activePet?: Pet;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public activePet$!: Observable<any>;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	constructor(
		private readonly appState: ApplicationQuery,
		private readonly brandQuery: BrandConfigQuery,
		private readonly router: Router,
		private readonly fb: FormBuilder,
		private readonly orchestratorService: OrchestratorService,
		private readonly sessionActions: QuoteSessionService,
		private readonly session: QuoteSessionQuery,
		@Inject(MAT_DIALOG_DATA) public data: ModalData
	) {
		this.brandUnformattedPhoneNumber$ = this.brandQuery.phone$();
		this.brandFormattedPhoneNumber$ = this.brandQuery
			.phone$()
			.pipe(map((phone) => formatPhoneNumber(phone)));
	}

	ngOnInit(): void {
		this.appState.activePet$().pipe(takeUntil(this.ngUnsubscribe$)).subscribe((petId) => {
			if (!!petId) {
				this.activePet$ = this.session.getPetById$(petId);
			}

			this.activePet$
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe((activePet) => {
					this.activePet = activePet;

					if (!this.activePet) {
						return;
					}

					const {
						examFeeCoverageOptions,
						drugDentalOptions,
					} = this.activePet;

					this.isExamFeesToggledOn = this.activePet?.examFeeCoverage ?? true;
					this.isRxCoverageToggledOn = this.activePet?.drugDental ?? true;
					this.isRxCoverageAmountVisible = this.activePet.supplementalType !== '2' || false;
					this.isExamFeesVisible = this.getVisibleCoverages(examFeeCoverageOptions).length > 0;
					this.isRxCoverageVisible = this.getVisibleCoverages(drugDentalOptions).length > 0;
				});
		});

		this.policyOptionsForm = this.fb.group({
			annualReimbursement: [
				null,
				{ validators: Validators.required, updateOn: 'change' },
			],
			annualDeductible: [null, Validators.required],
			reimbursementPercent: [null, Validators.required],
		});

		this.data.activePet.subscribe((pet: Pet) => {
			const {
				annualReimbursementValue,
				annualReimbursement,
				annualDeductibleValue,
				annualDeductible,
				reimbursementValue,
				reimbursement
			} = pet;
			this.reimbursementLimit = this.getVisibleCoverages(annualReimbursement);
			this.annualDeductible = this.getVisibleCoverages(annualDeductible);
			this.reimbursementPercent = this.getVisibleCoverages(reimbursement);

			this.policyOptionsForm.patchValue(
				{
					annualReimbursement: annualReimbursementValue,
					annualDeductible: annualDeductibleValue,
					reimbursementPercent: reimbursementValue
				},
				{ emitEvent: false, onlySelf: true }
			);
		});

		this.appState.activePet$().subscribe(petId => {
			this.activePet$ = this.session.getPetById$(petId as string);
		});
	}

	// eslint-disable-next-line
  public updateCoverage(controlName: string, value: any): void {
		try {
			this.updateCoverageSelection({
				[`${controlName}Value`]: value
			});
		} catch (err) {
			console.error(err);
		}
	}

	/**
	 * Used to update the Optional Coverages on/off toggles
	 *
	 * @param controlName The name of the control used to access via `FormGroup.get()`
	 * @param value the value passed from the control
	 */
	// eslint-disable-next-line
  public updateOptionalCoverage(controlName: string, value: any): void {
		try {
			this.updateCoverageSelection({
				[`${controlName}Value`]: value
			});
			if (controlName.toLowerCase() === 'examfeecoverage') {
				this.isExamFeesToggledOn = !this.isExamFeesToggledOn;
			} else {
				this.isRxCoverageToggledOn = !this.isRxCoverageToggledOn;
			}
		} catch (err) {
			console.error(err);
		}
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}

	private getVisibleCoverages(coverageOptions: CoverageOption[] = []): CoverageOption[] {
		return coverageOptions.filter(option => option.isVisible) ?? [];
	}

	private async updateCoverageSelection(
		coverageOption: Partial<
		Pick<Pet, 'annualDeductibleValue' | 'annualReimbursementValue' | 'reimbursementValue' | 'wellnessValue'>
		>
	): Promise<void> {
		const activePet = await firstValueFrom(this.data.activePet);
		if (!activePet) return;

		this.recalculating$.next(true);

		const {
			name,
			species,
			gender,
			breedId,
			breedName,
			ageInYears,
			isServiceDog,
			petId,
			annualDeductibleValue,
			annualReimbursementValue,
			reimbursementValue,
			wellnessValue,
			examFeeCoverage,
			examFeeCoverageOptions,
			drugDental,
			drugDentalOptions,
		} = activePet as Pet;
		const request: Pet = {
			name,
			species,
			gender,
			breedId,
			breedName,
			ageInYears,
			petId,
			isServiceDog,
			annualDeductibleValue,
			annualReimbursementValue,
			reimbursementValue,
			wellnessValue,
			examFeeCoverage,
			examFeeCoverageOptions,
			drugDental,
			drugDentalOptions,
			...coverageOption
		};

		this.orchestratorService
			.updatePet(request)
			.pipe(first())
			.subscribe(
				({ quoteDetails, sessionType, id, pets, paymentSchedules }) => {
					this.sessionActions.update({
						quoteDetails: {
							...quoteDetails,
							hadVetVisit: this.session.getVetVisit()
						},
						sessionType,
						id,
						pets,
						paymentSchedules,
					});
					this.recalculating$.next(false);
				},
				err => {
					console.warn(err);
					this.router.navigate([ROUTES.ERROR]);
				}
			);
	}
}
